import React, { useState } from 'react';
import { Trash2, Plus, Minus } from 'lucide-react';
import clsx from 'clsx';
import { useCart } from '@context/CartContext';
import { formatCurrency } from '@utils/helpers';
import { PRICING } from '@utils/constants';
import Button from '@components/common/Button';
import ResponsiveImage from '@components/common/ResponsiveImage';
import type { CartItem as CartItemType } from '@/types';

interface CartItemProps {
  item: CartItemType;
  className?: string;
}

const CartItem: React.FC<CartItemProps> = ({ item, className }) => {
  const { updateItem, removeFromCart } = useCart();
  const [isUpdating, setIsUpdating] = useState(false);

  const handleQuantityChange = async (newQuantity: number) => {
    if (newQuantity <= 0) {
      handleRemove();
      return;
    }

    setIsUpdating(true);
    try {
      updateItem(item.id, { quantity: newQuantity });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemove = () => {
    removeFromCart(item.id);
  };

  const handleResolutionChange = (newResolution: 'web' | 'print' | 'full') => {
    const newPrice = PRICING.resolutions[newResolution].price;
    updateItem(item.id, {
      resolution: newResolution,
      price: newPrice,
    });
  };

  const resolution = PRICING.resolutions[item.resolution as keyof typeof PRICING.resolutions];
  const totalPrice = item.price * item.quantity;

  return (
    <div
      className={clsx(
        'bg-white rounded-lg border border-gray-200 p-4 transition-all duration-200',
        'hover:shadow-md',
        className
      )}
    >
      <div className="flex gap-4">
        {/* Thumbnail */}
        <div className="w-20 h-20 flex-shrink-0">
          <ResponsiveImage
            src={item.thumbnail}
            alt={item.title}
            className="w-full h-full rounded-lg"
          />
        </div>

        {/* Item details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-medium text-gray-900 truncate pr-2">{item.title}</h3>
            <Button
              size="small"
              variant="ghost"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
              onClick={handleRemove}
              icon={<Trash2 size={16} />}
              aria-label="Remove from cart"
            />
          </div>

          {/* Resolution selector */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">Resolution</label>
            <select
              value={item.resolution}
              onChange={e => handleResolutionChange(e.target.value as 'web' | 'print' | 'full')}
              className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={isUpdating}
            >
              {Object.values(PRICING.resolutions).map(res => (
                <option key={res.key} value={res.key}>
                  {res.name} - {formatCurrency(res.price)}
                </option>
              ))}
            </select>
          </div>

          {/* Quantity and price */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                size="small"
                variant="outline"
                onClick={() => handleQuantityChange(item.quantity - 1)}
                disabled={isUpdating || item.quantity <= 1}
                icon={<Minus size={12} />}
                className="w-8 h-8 p-0"
              />
              <span className="w-8 text-center font-medium">{item.quantity}</span>
              <Button
                size="small"
                variant="outline"
                onClick={() => handleQuantityChange(item.quantity + 1)}
                disabled={isUpdating}
                icon={<Plus size={12} />}
                className="w-8 h-8 p-0"
              />
            </div>

            <div className="text-right">
              <div className="font-semibold text-gray-900">{formatCurrency(totalPrice)}</div>
              {item.quantity > 1 && (
                <div className="text-xs text-gray-500">{formatCurrency(item.price)} each</div>
              )}
            </div>
          </div>

          {/* Resolution info */}
          {resolution && (
            <div className="mt-2 text-xs text-gray-500">
              {resolution.description} • {resolution.dimensions}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartItem;
